import { OpenInNew } from "@mui/icons-material";
import { Button } from "@mui/material";
import { format, parseISO } from "date-fns";
import AccountModuleEntryCard from "./AccountModuleEntryCard";
import { AccountEmptyState, AccountPanelHeader } from "./accountEditorUi";
import {
  getInsuranceCommissionSummary,
  isAccountRowCompleted,
  isOshcInsuranceRow,
} from "../accountUtils";
import { isLinkedModuleRow } from "../modulePaymentsRowUtils";

const MODULE_COPY = {
  Visa: {
    singular: "Visa application",
    description:
      "Applications synced from Visa. Record client payments and keep charges up to date.",
    emptyTitle: "No visa applications on this account",
    emptyDescription:
      "Open Visa for this client to create or link an application. It will appear here automatically.",
  },
  Insurance: {
    singular: "Insurance policy",
    description:
      "Policies synced from Insurance. Record client payments here, and commission when the insurer pays.",
    emptyTitle: "No insurance policies on this account",
    emptyDescription:
      "Open Insurance for this client to add a policy. It will appear here automatically.",
  },
  "Skill Assessment": {
    singular: "Skill assessment",
    description:
      "Assessments synced from Skill Assessment. Record client payments and keep charges up to date.",
    emptyTitle: "No skill assessments on this account",
    emptyDescription:
      "Open Skill Assessment for this client to add a record. It will appear here automatically.",
  },
  RPL: {
    singular: "RPL certificate",
    description:
      "Certificates synced from RPL. Record client payments and keep charges up to date.",
    emptyTitle: "No RPL records on this account",
    emptyDescription:
      "Open RPL for this client to add a certificate. It will appear here automatically.",
  },
};

const formatMonthLabel = (month) => {
  if (!month) return null;
  try {
    return format(parseISO(`${month}-01`), "MMM yyyy");
  } catch {
    return month;
  }
};

const AccountLinkedModulePanel = ({
  moduleLabel,
  rows = [],
  moduleConfig,
  clientId,
  onManageModule,
  onOpenPayments,
  onOpenCharges,
  onOpenCommission,
  onOpenNotes,
  onOpenExternal,
  onOpenPendingVerifications,
  onToggleCommissionCompleted,
  commissionToggleBusyId,
  canReadVerifications,
}) => {
  const isInsurance = moduleLabel === "Insurance";
  const copy = MODULE_COPY[moduleLabel] || {
    singular: `${moduleLabel} record`,
    description: `Synced from the client's ${moduleLabel} records.`,
    emptyTitle: `No ${moduleLabel.toLowerCase()} entries yet`,
    emptyDescription: `Open ${moduleLabel} for this client to add a record.`,
  };

  return (
    <div className="space-y-4">
      <AccountPanelHeader
        title={moduleLabel}
        description={copy.description}
        count={rows.length}
        action={
          moduleConfig && clientId ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<OpenInNew />}
              onClick={() => onManageModule?.(moduleConfig)}
            >
              Open {moduleLabel}
            </Button>
          ) : null
        }
      />

      {rows.length === 0 ? (
        <AccountEmptyState
          title={copy.emptyTitle}
          description={copy.emptyDescription}
          actionLabel={moduleConfig && clientId ? `Open ${moduleLabel}` : undefined}
          onAction={moduleConfig && clientId ? () => onManageModule?.(moduleConfig) : undefined}
          actionIcon={<OpenInNew />}
        />
      ) : (
        <div className="space-y-3">
          {/* <p className="text-[11px] font-semibold uppercase tracking-wide text-app-text-muted m-0">
            {rows.length === 1 ? copy.singular : `${copy.singular}s`}
          </p> */}

          {rows.map((row, i) => {
            const entryNumber = i + 1;
            const completed = isAccountRowCompleted(row);
            const pending = canReadVerifications && row.hasPendingModulePayment ? 1 : 0;
            const summary = isInsurance ? getInsuranceCommissionSummary(row) : null;
            const oneTimeCommission = isInsurance && isOshcInsuranceRow(row);
            const lastMonth = formatMonthLabel(summary?.lastMonth);
            const hasSummary = Boolean(row.linkedModuleSummary?.trim());
            const title = hasSummary
              ? row.linkedModuleSummary.trim()
              : `${copy.singular} ${entryNumber}`;
            const subtitle = hasSummary
              ? `${copy.singular} ${entryNumber}`
              : "No identifying details on file";

            const commissionMeta =
              isInsurance && summary?.count
                ? [
                    {
                      label: oneTimeCommission ? "Commission" : "Months",
                      value: oneTimeCommission
                        ? "One-time"
                        : lastMonth
                        ? `${summary.count} · last ${lastMonth}`
                        : String(summary.count),
                    },
                  ]
                : [];

            return (
              <AccountModuleEntryCard
                key={row.uuid}
                index={entryNumber}
                title={title}
                subtitle={subtitle}
                meta={commissionMeta}
                agreed={row.agreedAmount}
                paid={row.paidAmount}
                due={row.dueAmount}
                cost={row.cost}
                mara={row.agreed}
                referral={row.referral}
                showMara={moduleLabel === "Visa" || moduleLabel === "Skill Assessment"}
                showReferral
                commission={summary?.total ?? row.commission}
                showCommission={isInsurance}
                profitLoss={row.profitLoss}
                completed={completed}
                pendingCount={pending}
                onPendingClick={
                  pending && onOpenPendingVerifications
                    ? () => onOpenPendingVerifications(row)
                    : undefined
                }
                onOpenPayments={row.id ? () => onOpenPayments?.(row) : undefined}
                paymentsVariant={isInsurance && !summary?.count ? "outlined" : "contained"}
                onOpenCharges={isLinkedModuleRow(row) ? () => onOpenCharges?.(row) : undefined}
                onOpenCommission={isInsurance ? () => onOpenCommission?.(row) : undefined}
                commissionCompleted={Boolean(row.commissionCompleted)}
                onToggleCommissionCompleted={
                  isInsurance && row.id
                    ? (next) => onToggleCommissionCompleted?.(row, next)
                    : undefined
                }
                commissionToggleBusy={commissionToggleBusyId === row.id}
                commissionActionLabel={
                  summary?.count ? `Commission (${summary.count})` : "Record commission"
                }
                commissionPrimary={isInsurance && !summary?.count}
                onOpenNotes={() => onOpenNotes?.(row)}
                onOpenExternal={() => onOpenExternal?.(row)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountLinkedModulePanel;
